/**
 * WalletConnect adapter for client-signing XRPL wallets — Joey today; the same
 * adapter reaches Bifrost/Girin/any xrpl-namespace WC wallet (multi-wallet Phase 4).
 *
 * Spike-verified Joey behaviors this adapter assumes:
 * - methods: xrpl_signTransaction / xrpl_signTransactionFor / xrpl_signTransactionBulk
 * - the wallet ALWAYS broadcasts what it signs (options.submit is ignored)
 * - responses come back as { tx_json: { ...signedFields, hash } }, not a hex blob
 * - the app silently pins sessions to its active network: ALWAYS validate the
 *   granted chain after approval instead of trusting the proposal
 */

import type UP from '@walletconnect/universal-provider';

const CHAIN: string = import.meta.env.VITE_XRPL_WC_CHAIN || 'xrpl:0';
const WC_METHODS = ['xrpl_signTransaction', 'xrpl_signTransactionFor', 'xrpl_signTransactionBulk'];
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

type WcProvider = InstanceType<typeof UP>;
let providerPromise: Promise<WcProvider> | null = null;

async function getProvider(): Promise<WcProvider> {
  if (!providerPromise) {
    providerPromise = (async () => {
      const projectId = import.meta.env.VITE_REOWN_PROJECT_ID;
      if (!projectId) {
        throw new Error('WalletConnect is not configured (VITE_REOWN_PROJECT_ID missing)');
      }
      const { UniversalProvider } = await import('@walletconnect/universal-provider');
      const provider = await UniversalProvider.init({
        projectId,
        metadata: {
          name: 'XRP Deals',
          description: 'XRPL NFT marketplace',
          url: window.location.origin,
          icons: [`${window.location.origin}/favicon-32x32.png`],
        },
      });
      // The wallet can end the pairing from its side (user disconnects, or it lapses).
      // The SDK clears provider.session on these, so getWcSession() reflects it; we also
      // broadcast a window event so the app can react (e.g. prompt to reconnect).
      const onSessionEnded = () => window.dispatchEvent(new CustomEvent('wc:session_ended'));
      provider.on('session_delete', onSessionEnded);
      provider.on('session_expire', onSessionEnded);
      return provider;
    })();
    providerPromise.catch(() => {
      providerPromise = null; // allow retry after init failure
    });
  }
  return providerPromise;
}

export interface WcSession {
  address: string;
  chain: string;
}

/** Existing approved session, if any (survives reloads via WC's IndexedDB storage). */
export async function getWcSession(): Promise<WcSession | null> {
  const provider = await getProvider();
  const accounts: string[] = provider.session?.namespaces?.xrpl?.accounts ?? [];
  const match = accounts.find((a) => a.startsWith(`${CHAIN}:`));
  return match ? { address: match.split(':').pop()!, chain: CHAIN } : null;
}

/**
 * Open a WalletConnect session. onPairingUri fires with the wc: URI to render as a QR.
 * Resolves once the wallet approves with an account on the expected chain.
 */
export async function connectWc(onPairingUri: (uri: string) => void): Promise<WcSession> {
  const provider = await getProvider();

  const existing = await getWcSession();
  if (existing) return existing;

  const exposeUri = (uri: string) => {
    // Test/debug hook: lets automated tests (headless WC wallet) read the pairing URI
    (window as any).__wcPairingUri = uri;
    onPairingUri(uri);
  };
  provider.on('display_uri', exposeUri);
  try {
    const session = await provider.connect({
      namespaces: {
        xrpl: { methods: ['xrpl_signTransaction'], chains: [CHAIN], events: ['chainChanged', 'accountsChanged'] },
      },
      optionalNamespaces: {
        xrpl: { methods: WC_METHODS, chains: ['xrpl:0', 'xrpl:1', 'xrpl:2'], events: ['chainChanged', 'accountsChanged'] },
      },
    });
    const accounts: string[] = session?.namespaces?.xrpl?.accounts ?? [];
    const match = accounts.find((a) => a.startsWith(`${CHAIN}:`));
    if (!match) {
      await provider.disconnect().catch(() => undefined);
      throw new Error(
        'Your wallet is on a different XRPL network. Switch networks in the wallet app and try again.'
      );
    }
    return { address: match.split(':').pop()!, chain: CHAIN };
  } finally {
    provider.off('display_uri', exposeUri);
  }
}

export async function disconnectWc(): Promise<void> {
  const provider = await getProvider();
  await provider.disconnect().catch(() => undefined);
}

export interface WcSignResult {
  /** Deterministic tx hash (present in Joey responses; wallets that submit return it). */
  hash?: string;
  /** Full signed tx_json when the wallet returns one (Joey does). */
  signedTxJson?: Record<string, unknown>;
  raw: unknown;
}

function extractHash(resp: unknown): string | undefined {
  const seen = new Set<object>();
  const stack: unknown[] = [resp];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur as object)) continue;
    seen.add(cur as object);
    for (const [k, v] of Object.entries(cur as Record<string, unknown>)) {
      if (typeof v === 'string' && /^(hash|id|tx_id|txid)$/i.test(k) && /^[A-F0-9]{64}$/i.test(v)) return v;
      if (v && typeof v === 'object') stack.push(v);
    }
  }
  return undefined;
}

/**
 * Ask the connected wallet to sign (and, in practice, broadcast) a transaction.
 * Rejection in the wallet surfaces as a thrown error.
 */
export async function wcSignTransaction(txJson: Record<string, unknown>): Promise<WcSignResult> {
  const provider = await getProvider();
  if (!provider.session) throw new Error('No active WalletConnect session');

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, rej) => {
    timer = setTimeout(() => rej(new Error('Timed out waiting for wallet approval')), REQUEST_TIMEOUT_MS);
  });
  const request = provider.request(
    { method: 'xrpl_signTransaction', params: { tx_json: txJson, options: { autofill: true, submit: true } } },
    CHAIN
  );
  // If the timeout wins the race, the request promise still settles later — swallow it so
  // its eventual rejection isn't an unhandled rejection.
  request.catch(() => {});

  try {
    const resp = await Promise.race([request, timeout]);
    const signedTxJson = (resp as any)?.tx_json && typeof (resp as any).tx_json === 'object' ? (resp as any).tx_json : undefined;
    return { hash: extractHash(resp), signedTxJson, raw: resp };
  } finally {
    if (timer) clearTimeout(timer); // don't leak the 5-minute timer when the request wins
  }
}
